import { useState, useEffect } from 'react';
import { useThemeColor } from '@/hooks/useThemeColor';

interface CountdownTimerProps {
  endAt: string;
  onExpire?: () => void;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calcTimeLeft(endAt: string): TimeLeft {
  const diff = new Date(endAt).getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

export default function CountdownTimer({ endAt, onExpire }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calcTimeLeft(endAt));
  const { primary, hexAlpha } = useThemeColor();

  useEffect(() => {
    const interval = setInterval(() => {
      const t = calcTimeLeft(endAt);
      setTimeLeft(t);
      if (t.days === 0 && t.hours === 0 && t.minutes === 0 && t.seconds === 0) {
        clearInterval(interval);
        onExpire?.();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [endAt, onExpire]);

  const pad = (n: number) => String(n).padStart(2, '0');
  const isUrgent = timeLeft.days === 0 && timeLeft.hours < 3;

  const units = [
    { label: 'يوم', value: timeLeft.days },
    { label: 'ساعة', value: timeLeft.hours },
    { label: 'دقيقة', value: timeLeft.minutes },
    { label: 'ثانية', value: timeLeft.seconds },
  ];

  return (
    <div className="flex items-center gap-3 flex-wrap justify-center" dir="rtl">
      {units.map((unit, idx) => (
        <div key={unit.label} className="flex items-center gap-3">
          <div className="flex flex-col items-center">
            <div
              className="relative w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-2xl font-black text-2xl md:text-3xl tabular-nums overflow-hidden"
              style={{
                fontFamily: "'Amiri', 'Tajawal', serif",
                color: isUrgent ? '#f87171' : '#ffffff',
                background: isUrgent
                  ? 'linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(239,68,68,0.05) 100%)'
                  : `linear-gradient(135deg, ${hexAlpha(primary, 0.12)} 0%, ${hexAlpha(primary, 0.04)} 100%)`,
                border: isUrgent
                  ? '1px solid rgba(239,68,68,0.35)'
                  : `1px solid ${hexAlpha(primary, 0.25)}`,
                boxShadow: isUrgent
                  ? '0 0 20px rgba(239,68,68,0.1), inset 0 1px 0 rgba(239,68,68,0.1)'
                  : `0 0 20px ${hexAlpha(primary, 0.08)}, inset 0 1px 0 ${hexAlpha(primary, 0.1)}`,
                animation: isUrgent ? 'ebdaa-pulse-red 1s ease-in-out infinite' : undefined,
              }}
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
              <span className="relative z-10">{pad(unit.value)}</span>
            </div>
            <span
              className="text-xs mt-2 tracking-wide"
              style={{ fontFamily: "'Tajawal', sans-serif", color: isUrgent ? 'rgba(248,113,113,0.7)' : hexAlpha(primary, 0.6) }}
            >
              {unit.label}
            </span>
          </div>

          {idx < units.length - 1 && (
            <div className="flex flex-col gap-2 mb-5">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: isUrgent ? '#f87171' : primary,
                  boxShadow: isUrgent ? '0 0 6px rgba(239,68,68,0.8)' : `0 0 6px ${primary}`,
                }}
              ></span>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: isUrgent ? '#f87171' : primary,
                  boxShadow: isUrgent ? '0 0 6px rgba(239,68,68,0.8)' : `0 0 6px ${primary}`,
                }}
              ></span>
            </div>
          )}
        </div>
      ))}

      <style>{`
        @keyframes ebdaa-pulse-red {
          0%, 100% { box-shadow: 0 0 20px rgba(239,68,68,0.1); }
          50% { box-shadow: 0 0 30px rgba(239,68,68,0.25); }
        }
      `}</style>
    </div>
  );
}
